/**
 * BulkSynopsisImportDialog
 * Admin-only dialog to upload the Crown & Rose multi-synopsis DOCX and
 * create one project frame (project + framework + characters + chapter stubs)
 * per book in a single operation.
 */
import { useState, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileUp, CheckCircle2, AlertCircle, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STRUCTURE_OPTIONS = [
  {
    value: '3-act',
    label: '3-Act',
    desc: 'Industry standard — Setup (0–25%), Confrontation (25–75%), Resolution (75–100%). Best for commercial fiction.',
  },
  {
    value: '5-act',
    label: '5-Act',
    desc: "Freytag's Pyramid — Climax sits dead-centre (~50%), not the end. Popular in prestige TV and Shakespearean drama.",
  },
  {
    value: '7-act',
    label: '7-Point',
    desc: "Dan Wells' 7-Point — Hook → Plot Turn 1 → Pinch Point 1 → Midpoint → Pinch Point 2 → Plot Turn 2 → Resolution.",
  },
];

export function BulkSynopsisImportDialog({ open, onClose }) {
  const fileRef       = useRef(null);
  const [file, setFile]           = useState(null);
  const [structure, setStructure] = useState('3-act');
  const [dryRun, setDryRun]       = useState(true);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy]           = useState(false);
  const [result, setResult]       = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setBusy(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
    setResult(null);
  };

  const handleImport = async () => {
    if (!file) { toast.error('Choose a DOCX file first.'); return; }
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('dry_run', dryRun ? 'true' : 'false');
      form.append('overwrite', overwrite ? 'true' : 'false');
      form.append('structure', structure);

      const { data } = await axios.post(
        `${API}/api/admin/synopsis/bulk-import-docx`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );
      setResult(data);
      if (!dryRun) {
        toast.success(`${data.created} project frames created.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-lg" data-testid="bulk-synopsis-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="h-4 w-4 text-amber-500" />
            Bulk Synopsis Import
          </DialogTitle>
          <DialogDescription className="text-xs">
            Upload the Crown &amp; Rose multi-synopsis DOCX. One project frame is created
            per book — project, framework, characters and chapter stubs.
            Run as a <strong>dry run</strong> first to preview what will be created.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">

          {/* Structure selector */}
          <div>
            <p className="text-xs font-medium text-foreground mb-2">Act structure for all projects</p>
            <div className="grid grid-cols-3 gap-2">
              {STRUCTURE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  title={opt.desc}
                  onClick={() => setStructure(opt.value)}
                  className={`rounded-md border px-3 py-2 text-xs font-semibold transition-colors
                    ${structure === opt.value
                      ? 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                      : 'border-border text-muted-foreground hover:border-amber-300'
                    }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
              {STRUCTURE_OPTIONS.find(o => o.value === structure)?.desc}
            </p>
          </div>

          {/* File picker */}
          <div>
            <p className="text-xs font-medium text-foreground mb-2">DOCX file</p>
            <div
              className="flex items-center gap-3 rounded-md border border-dashed border-border p-3 cursor-pointer hover:border-amber-400 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              <FileUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-xs text-muted-foreground truncate">
                {file ? file.name : 'Click to choose Crown_and_Rose_Story_Synopses.docx'}
              </span>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Options */}
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={e => setDryRun(e.target.checked)}
                className="accent-amber-500"
              />
              Dry run (preview only)
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={overwrite}
                onChange={e => setOverwrite(e.target.checked)}
                className="accent-amber-500"
              />
              Overwrite existing projects
            </label>
          </div>

          {/* Result */}
          {result && (
            <div className="rounded-md border border-border bg-muted/40 p-3 space-y-1.5 max-h-60 overflow-y-auto">
              <div className="flex items-center gap-2 text-xs font-medium">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                Parsed {result.total} books —{' '}
                {dryRun ? 'dry run, no writes' : `${result.created} created, ${result.skipped} skipped, ${result.errors} errors`}
              </div>
              {result.details?.map((d, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px]">
                  {d.status === 'error'
                    ? <AlertCircle className="h-3 w-3 text-rose-400 mt-0.5 flex-shrink-0" />
                    : d.status === 'skipped'
                    ? <AlertCircle className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" />
                    : <BookOpen className="h-3 w-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                  }
                  <span className="text-foreground font-medium">{d.title}</span>
                  {d.status === 'error' && (
                    <span className="text-rose-400 ml-1">— {d.reason}</span>
                  )}
                  {d.status === 'skipped' && (
                    <span className="text-amber-400 ml-1">— skipped</span>
                  )}
                  {d.characters !== undefined && (
                    <span className="text-muted-foreground ml-auto">
                      {d.characters} chars · {d.chapter_stubs} chs
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleImport}
            disabled={!file || busy}
            data-testid="bulk-synopsis-import-submit"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {busy
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Importing…</>
              : dryRun ? 'Preview import' : 'Create project frames'
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
